import classNames from 'classnames'
import {
  forwardRef,
  MouseEventHandler,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigation } from 'react-router'
import { useActionError } from '~/api/useActionError'
import { ActionErrorAlert } from '~/components/ActionErrorAlert'
import { Button, ButtonLink, ButtonProps } from '~/components/Button'
import { useLatestFormIntent } from '~/utils/formAction'
import { gapClasses } from '~/utils/layout'

import { ShowSuccessButton } from './ShowSuccessButton'

const TEXT = {
  continue: 'Weiter',
  back: 'Zurück',
  confirm: 'Änderung bestätigen',
  reset: 'Änderung verwerfen',
  cancel: 'Abbrechen',
}

export const actionBarClasses = {
  actionBar: classNames('flex flex-row-reverse justify-end', gapClasses.gap),
  // We display the primary action right, although it should sit
  // at first position in the dom
  primaryAction: 'sm:col-start-2 ',
}

const useShowSuccess = (): boolean => {
  const navigation = useNavigation()
  const navigationState = navigation.state

  const formDataRef = useRef<FormData | undefined>(undefined)
  const actionError = useActionError()
  const formData = navigation.formData

  const [showSuccess, setShowSuccess] = useState(false)

  if (navigationState === 'submitting') {
    formDataRef.current = formData
  }

  useEffect(() => {
    if (
      navigationState === 'idle' && // loading has finished
      formDataRef.current && // and a form has been submitted
      !actionError // without errors
    ) {
      formDataRef.current = undefined
      setTimeout(() => {
        setShowSuccess(true)
      }, 200)
      const timeout = setTimeout(() => {
        setShowSuccess(false)
      }, 3000)
      return () => clearTimeout(timeout)
    }
  }, [navigationState, actionError])

  return showSuccess
}

const useIsLoading = (): boolean => {
  const navigation = useNavigation()
  const navigationState = navigation.state

  return navigationState !== 'idle'
}

/**
 * Determines if an element has been clicked since isLoading has changed from false to true.
 * As soon isLoading is false, the state will be reset.
 *
 * This is useful to determine if a button has caused a loading state.
 *
 * Note: A click won't trigger a re-render. The state change must be initiated by isLoading.
 * */
const useWasClickedSinceLoading = (
  isLoading: boolean,
): [boolean, () => void] => {
  const clickedRef = useRef(false)

  useEffect(() => {
    if (!isLoading) {
      clickedRef.current = false
    }
  }, [isLoading])

  const handleClick = () => {
    clickedRef.current = true
  }

  return [clickedRef.current, handleClick]
}

export type ContinueButtonProps = Pick<ButtonProps, 'disabled' | 'form'> & {
  supressLoadingStatus?: boolean
  children?: React.ReactNode
}

export const ContinueButton = forwardRef<
  HTMLButtonElement,
  ContinueButtonProps
>(
  (
    { supressLoadingStatus = false, children = TEXT.continue, disabled, form },
    ref,
  ) => {
    const isLoading = useIsLoading()
    const [wasClickedSinceLoading, handleClick] =
      useWasClickedSinceLoading(isLoading)

    const showLoading =
      isLoading &&
      !supressLoadingStatus &&
      // only show loading spinner if the button has caused the loading
      wasClickedSinceLoading

    return (
      <Button
        ref={ref}
        form={form}
        type='submit'
        className={actionBarClasses.primaryAction}
        disabled={disabled}
        isLoading={showLoading}
        onClick={handleClick}
      >
        {children}
      </Button>
    )
  },
)
ContinueButton.displayName = 'ContinueButton'

export const SaveChangesButton = forwardRef<
  HTMLButtonElement,
  { supressLoadingStatus?: boolean; children?: React.ReactNode }
>(({ supressLoadingStatus = false, children = TEXT.confirm }, ref) => {
  const isLoading = useIsLoading()
  const showSuccess = useShowSuccess()

  return (
    <ShowSuccessButton
      ref={ref}
      type='submit'
      className={actionBarClasses.primaryAction}
      isLoading={isLoading && !supressLoadingStatus}
      showSuccess={showSuccess}
    >
      {children}
    </ShowSuccessButton>
  )
})
SaveChangesButton.displayName = 'SubmitButton'

export const ContinueButtonLink = ({ to }: { to: string }) => {
  return (
    <ButtonLink to={to} className={actionBarClasses.primaryAction}>
      {TEXT.continue}
    </ButtonLink>
  )
}

export const BackButtonLink = ({ to }: { to: string }) => {
  return (
    <ButtonLink variant='ghost' to={to}>
      {TEXT.back}
    </ButtonLink>
  )
}

export const CancelButton = ({
  onClick,
  children,
}: {
  onClick: ButtonProps['onClick']
  children?: React.ReactNode
}) => {
  return (
    <Button variant='ghost' onClick={onClick}>
      {children || TEXT.cancel}
    </Button>
  )
}

export type ActionBarProps = {
  children: React.ReactNode
  hideActionErrorAlert?: boolean
  intent?: string
  classes?: string
  isSuccess?: boolean
}

export const BaseActionBar = ({ children }: { children: React.ReactNode }) => {
  return <div className={actionBarClasses.actionBar}>{children}</div>
}

/**
 * This component combines an action button, action intent and error alert.
 * - The intent can be used to distinguish between different forms on the same page
 * - If an intent is passed, only action errors will be shown, if this intent was submitted last
 * - If NO intent is passed, only general action errors (without intent) will be shown
 * */
export const ActionBar = ({
  children,
  hideActionErrorAlert = false,
  intent,
  classes,
  isSuccess = false,
}: ActionBarProps) => {
  const navigation = useNavigation()
  const actionError = useActionError()
  const lastSubmittedIntent = useLatestFormIntent()
  const showError =
    !isSuccess &&
    actionError &&
    !hideActionErrorAlert &&
    // we hide the error during submission or loading to prevent the errors popping up
    // when there was an error before for another intent
    navigation.state === 'idle'
  return (
    <div className={classNames('flex flex-col', gapClasses.gap, classes)}>
      <BaseActionBar>{children}</BaseActionBar>
      {intent && <input type='hidden' name='intent' value={intent} />}
      {intent === undefined
        ? // Case 1: ActionBar is NOT specific to an intent --> show all errors
          showError && <ActionErrorAlert data={actionError} />
        : // Case 2: Action bar is specific to an intent --> only show errors if intent was last submitted last
          intent === lastSubmittedIntent &&
          showError && <ActionErrorAlert data={actionError} />}
    </div>
  )
}

export const DefaultActionBar = ({ backUrl }: { backUrl: string }) => {
  return (
    <ActionBar>
      <ContinueButton />
      <BackButtonLink to={backUrl} />
    </ActionBar>
  )
}

export type SettingsActionBarType = {
  submitButtonRef?: RefObject<HTMLButtonElement | null>
  supressLoadingStatus?: boolean
  onResetClick: MouseEventHandler<HTMLButtonElement>
  hideActionErrorAlert?: boolean
  intent?: string
  hideResetButton?: boolean
}

/**
 * This component differs from the DefaultActionBar
 * - Different wording
 * - "Reset" button instead of "back" button (requires click handler instead of url)
 * */
export const SettingsActionBar = ({
  submitButtonRef,
  onResetClick,
  hideActionErrorAlert,
  intent,
  hideResetButton,
}: SettingsActionBarType) => {
  return (
    <ActionBar intent={intent} hideActionErrorAlert={hideActionErrorAlert}>
      <SaveChangesButton ref={submitButtonRef}>
        {TEXT.confirm}
      </SaveChangesButton>
      {!hideResetButton && (
        <Button variant={'ghost'} onClick={onResetClick}>
          {TEXT.reset}
        </Button>
      )}
    </ActionBar>
  )
}
